import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChildren
} from '@angular/core'
import { MemberFormService } from '@features/insured/services/member/member-form.service'
import { InsuredDataFacade, SaveInsuredRequest, SaveInsuredResult } from '@features/insured/services/insured-data.facade'
import { FormAutosaveService } from '@services/form-autosave.service'
import { ActivatedRoute, Router } from '@angular/router'
import { UnsavedChangesGuard } from '@services/unsaved-changes.guard'
import { ModalHelperService } from '@services/modal-helper.service'
import { GlobalErrorService } from '@services/global-error.service'
import { InsuredSidebarStateService } from '@app/layout/sidebar/services/insured-sidebar-state.service'
import { DetailviewNavigationService, NavSection } from '@services/detailview-navigation.service'
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { catchError, distinctUntilChanged, EMPTY, map, Observable, tap } from 'rxjs'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { AlbertSectionNavigationComponent } from '@shared/components/albert-section-navigation/albert-section-navigation.component'
import { ButtonDirective } from 'primeng/button'
import { MemberApiResponse } from '@features/insured/models/member/member.model'
import { AviationHullApiResponse, AviationLiabilities, CommercialGeneralLiability } from '@features/insured/models/coverages/base-coverage.model'
import { CategoryService } from '@core/auth/services/category.service'
import { PermissionService } from '@core/auth/services/permission.service'
import { MemberComponent } from '@features/insured/components/member/member.component'
import { PolicyComponent } from '@features/insured/components/policy/policy.component'
import { PeriodComponent } from '@features/insured/components/period/period.component'
import { PolicyFormService } from '@features/insured/services/policy/policy-form.service'
import { PolicyApiResponse } from '@features/insured/models/policy/policy.model'
import { CoverageApiResponse } from '@features/insured/models/coverages/coverage-api.model'
import { PeriodFormService } from '@features/insured/services/period/period-form.service'
import { Accordion, AccordionContent, AccordionHeader, AccordionPanel } from 'primeng/accordion'
import { AlbertTextareaComponent } from '@shared/components/albert-textarea/albert-textarea.component'
import { hasActualValue, isAlbertValueObjectForm, normalizeOverrideValues, revalidateControlTree } from '@core/utils/form-utils'

import { CoverageFormService } from '@features/insured/services/coverages/coverage-form.service'
import { CoverageMapperService } from '@features/insured/services/coverages/coverage-mapper.service'
import { AviationLiabilitiesFormComponent } from '@features/insured/components/coverages/aviation-liabilities/aviation-liabilities-form.component'
import { AviationHullFormComponent } from '@features/insured/components/coverages/aviation-hull/aviation-hull-form.component'
import {
  CommercialGeneralLiabilityFormComponent
} from '@features/insured/components/coverages/commercial-general-liability/commercial-general-liability-form.component'
import { CERTIFICATE_CATEGORY_OPTIONS } from '@features/template/models/template.model'
import { FleetListComponent } from '@features/insured/components/fleet-list/fleet-list.component'
import { INSURED_SECTIONS } from '@features/insured/models/insured-sections.model'

/**
 * Differentiates the four save/edit cases for an insured:
 *
 *  - `NEW_INSURED`            : no member uuid yet → only manual Save is available; autosave is OFF.
 *  - `EXISTING_NO_EXTERNAL`   : existing member without an external policy id → user can override all
 *                               coverage/subcoverage fields; show all sections.
 *  - `PROPOSAL_WITH_EXTERNAL` : existing member with external id and proposal status → user can
 *                               override existing values; show only sections that already have values.
 *  - `POLICY_READONLY`        : status === 'POLICY' → everything below the general policy info is
 *                               disabled (current behaviour).
 */
export type SaveMode = 'NEW_INSURED' | 'EXISTING_NO_EXTERNAL' | 'PROPOSAL_WITH_EXTERNAL' | 'POLICY_READONLY'

function sameSections(current: NavSection[], next: NavSection[]): boolean {
  return current.length === next.length && current.every((section, index) =>
    section.label === next[index]?.label
    && section.mainIndex === next[index]?.mainIndex
    && section.subValue === next[index]?.subValue)
}

@Component({
  selector: 'albert-insured',
  standalone: true,
  providers: [DetailviewNavigationService],
  imports: [
    AlbertSectionNavigationComponent,
    ButtonDirective,
    ReactiveFormsModule,
    MemberComponent,
    PolicyComponent,
    PeriodComponent,
    AviationLiabilitiesFormComponent,
    AviationHullFormComponent,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
    AlbertTextareaComponent,
    CommercialGeneralLiabilityFormComponent,
    FleetListComponent
  ],
  templateUrl: './insured.component.html',
  styleUrl: '../../shared/styles/detailview.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InsuredComponent implements OnInit, AfterViewInit, OnDestroy {
  public readonly nav = inject(DetailviewNavigationService)
  public readonly sectionControl = new FormControl<string | null>(null)
  public readonly insuredDisplayName = signal<string>('')
  public readonly insuredDataFacade = inject(InsuredDataFacade)
  public readonly isCreateMode = this.insuredDataFacade.isCreateMode
  public readonly periodOptions = this.insuredDataFacade.periodOptions
  public readonly policyOptions = this.insuredDataFacade.policyOptions
  public readonly availableCoverages = signal<string[]>([])
  /** Top-level sections rendered as accordion panels; a section's position is its panel value and nav index */
  protected readonly sections = INSURED_SECTIONS
  protected readonly categoryService = inject(CategoryService)
  public readonly activeCategory = computed(() => {
    const activeCategory = this.categoryService.activeCertificateCategory()
    return CERTIFICATE_CATEGORY_OPTIONS.find(c => c.value === activeCategory?.toString()) ?? null
  })
  protected readonly permissionService = inject(PermissionService)
  public readonly canEdit = computed(() => {
    const cat = this.categoryService.activeCategory()
    return cat === null ? false : this.permissionService.canEdit(cat)
  })
  private readonly modalHelper = inject(ModalHelperService)
  private readonly memberFormService = inject(MemberFormService)
  public readonly memberForm = this.memberFormService.buildForm()
  private readonly policyFormService = inject(PolicyFormService)
  public readonly policyForm = this.policyFormService.buildForm()
  private readonly coverageFormService = inject(CoverageFormService)
  public readonly aviationLiabilitiesForm = this.coverageFormService.buildAviationLiabilitiesForm()
  public readonly aviationHullForm = this.coverageFormService.buildAviationHullForm()
  public readonly commercialGeneralLiabilityForm = this.coverageFormService.buildCommercialGeneralLiabilityForm()
  private readonly coverageMapper = inject(CoverageMapperService)
  private readonly periodFormService = inject(PeriodFormService)
  public readonly periodForm = this.periodFormService.buildForm()
  private readonly policies = this.insuredDataFacade.policies
  private readonly autosaveService = inject(FormAutosaveService)
  private readonly route = inject(ActivatedRoute)
  private readonly router = inject(Router)
  private readonly cdr = inject(ChangeDetectorRef)
  private readonly accordionPanels = viewChildren<ElementRef<HTMLElement>>('accordionPanel')
  private readonly destroyRef = inject(DestroyRef)
  private readonly unsavedGuard = inject(UnsavedChangesGuard)
  private readonly globalErrorService = inject(GlobalErrorService)
  private readonly sidebarState = inject(InsuredSidebarStateService)
  // -- Reactive snapshots of the relevant form controls so `saveMode` can be a `computed`. --
  private readonly memberUuid = toSignal(this.memberForm.controls.uuid.valueChanges, {
    initialValue: this.memberForm.controls.uuid.value
  })
  private readonly memberExternalId = toSignal(this.memberForm.controls.externalId.valueChanges, {
    initialValue: this.memberForm.controls.externalId.value
  })
  private readonly policyStatus = toSignal(this.policyForm.controls.status.valueChanges, {
    initialValue: this.policyForm.controls.status.value
  })
  public readonly saveMode = computed<SaveMode>(() => {
    if (this.policyStatus() === 'POLICY') return 'POLICY_READONLY'
    if (!hasActualValue(this.memberUuid())) return 'NEW_INSURED'
    return hasActualValue(this.memberExternalId()) ? 'PROPOSAL_WITH_EXTERNAL' : 'EXISTING_NO_EXTERNAL'
  })
  public readonly canAutosave = computed(() => {
    const mode = this.saveMode()
    return mode === 'EXISTING_NO_EXTERNAL' || mode === 'PROPOSAL_WITH_EXTERNAL'
  })
  private readonly memberFormStatus = toSignal(this.memberForm.statusChanges, {
    initialValue: this.memberForm.status
  })
  private readonly policyFormStatus = toSignal(this.policyForm.statusChanges, {
    initialValue: this.policyForm.status
  })
  public readonly canSave = computed(() =>
    this.canEdit() && this.memberFormStatus() !== 'INVALID' && this.policyFormStatus() !== 'INVALID'
  )
  private lastLoadedUuid: string | null = null

  constructor() {
    // Toggle coverage forms read-only when the policy moves to `POLICY` status.
    effect(() => {
      const readonly = this.saveMode() === 'POLICY_READONLY'
      this.toggleFormEnablement(readonly)
    })
  }

  public ngOnInit(): void {
    this.setupRouteListener()
    this.setupPeriodAndPolicyListeners()
    this.loadPeriods()

    this.unsavedGuard.register({
      condition: () => this.memberForm.get('uuid')?.value === null,
      confirmLeave: () => this.confirmLeave()
    })

    this.sectionControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(val => {
      if (val !== null) this.nav.goToSection(Number.parseInt(val, 10))
    })

    this.setupAutosave()
  }

  public ngAfterViewInit(): void {
    this.nav.initialize(this.sectionControl, this.accordionPanels)
    const firstOption = this.nav.sectionOptions()[0]
    if (firstOption) {
      this.sectionControl.setValue(firstOption.value, { emitEvent: false })
      this.nav.currentIndex.set(0)
    }
    this.cdr.markForCheck()
  }

  public ngOnDestroy(): void {
    this.nav.destroy()
    this.unsavedGuard.unregister()
  }

  public onSave(): void {
    if (this.memberForm.invalid || this.policyForm.invalid) {
      this.memberForm.markAllAsTouched()
      this.policyForm.markAllAsTouched()
      return
    }
    this.performSave$().subscribe({
      error: () => this.reportSaveError()
    })
  }

  private reportSaveError(): void {
    this.globalErrorService.reportError({
      subsystemName: 'Insured Saving',
      description: `The insured could not be saved.`
    })
  }

  private setupAutosave(): void {
    const reportError = () => () => {
      this.globalErrorService.reportError({
        subsystemName: 'Insured Autosave',
        description: `The data could not be saved automatically.`
      })
      return EMPTY
    }

    // One merged stream over all editable forms → one debounced, consolidated save.
    // Autosave is gated by `canAutosave()` so it never fires in NEW_INSURED or POLICY_READONLY.
    this.autosaveService.observeMany(
      [
        this.memberForm,
        this.policyForm,
        this.aviationHullForm,
        this.aviationLiabilitiesForm,
        this.commercialGeneralLiabilityForm
      ],
      () => this.performSave$().pipe(catchError(reportError())),
      this.destroyRef,
      {
        extraFilter: () => this.canAutosave(),
        ignoredPaths: [
          'additionalAddressInformation.checked',
          'parentCompany.checked',
          'general.sparesJointlyFor',
          'general.hullDeductibleJointlyFor',
          '**.additionalInfoContent.checked'
        ],
        onValidityBlockChange: invalidForms => this.markAutosaveBlockers(invalidForms)
      }
    )
  }

  private markAutosaveBlockers(invalidForms: FormGroup[]): void {
    if (invalidForms.length === 0) return

    invalidForms.forEach(form => form.markAllAsTouched())
    this.cdr.markForCheck()
  }

  private navigateToInsured(uuid: string): void {
    this.router.navigate(['/insured', uuid], { replaceUrl: true })
      .catch(() => {
        this.globalErrorService.reportError({
          subsystemName: 'Insured Navigation',
          description: `The insured data could not navigated to`
        })
      })
  }

  private load(uuid: string): void {
    this.insuredDataFacade.getInsured(uuid).subscribe({
      next: (data) => {
        // Server-driven hydration: patchValue keeps the form pristine, so the autosave
        // dirty-gate below will not fire a PUT cascade just from loading.
        this.memberForm.patchValue(data)
        // `patchValue` writes `localValue` before `externalValue`, so the conditional
        // `requiredWithoutExternalValue` validators still see an empty sibling. Re-run them
        // once the whole payload has landed.
        revalidateControlTree(this.memberForm)
        this.memberForm.markAsPristine()
        const loadedName = data.name.localValue
        this.memberFormService.addAsyncValidators(this.memberForm, data.category, loadedName)
        if (this.route.snapshot.paramMap.get('uuid') !== uuid) {
          this.navigateToInsured(uuid)
        }
        this.updateInsuredDisplayName()
        this.cdr.markForCheck()
      },
      error: () => {
        this.globalErrorService.reportError({
          subsystemName: 'Insured Loading',
          description: `The insured data could not be loaded.`
        })
        this.updateInsuredDisplayName()
        this.cdr.markForCheck()
      }
    })
  }

  /**
   * Build the save request from the current form snapshots and let the facade drive the chain
   * (member → policy → coverages → subcoverages). Side-effects (form patching, navigation, sidebar
   * reload) happen in a single `tap` so the pipe stays declarative.
   */
  private performSave$(): Observable<SaveInsuredResult> {
    const request = this.buildSaveRequest()
    return this.insuredDataFacade.saveInsured(request).pipe(
      tap(result => this.applySaveResult(result))
    )
  }

  private buildSaveRequest(): SaveInsuredRequest {
    const member = this.memberForm.getRawValue() as unknown as MemberApiResponse
    const request: SaveInsuredRequest = { member }

    const periodUuid = this.periodForm.controls.uuid.value
    if (hasActualValue(periodUuid)) {
      const policy = this.policyForm.getRawValue() as unknown as PolicyApiResponse
      policy.periodId = periodUuid
      request.policy = policy
    }

    // Only attempt coverage persistence when the mode actually allows editing coverages.
    if (this.canAutosave() || this.saveMode() === 'NEW_INSURED') {
      request.coverages = {
        aviationHull: this.aviationHullForm.getRawValue() as unknown as AviationHullApiResponse,
        aviationLiabilities: this.aviationLiabilitiesForm.getRawValue() as unknown as AviationLiabilities,
        commercialGeneralLiability: this.commercialGeneralLiabilityForm.getRawValue() as unknown as CommercialGeneralLiability
      }
    }

    return normalizeOverrideValues(request)
  }

  private applySaveResult(result: SaveInsuredResult): void {
    this.memberForm.patchValue(result.member, { emitEvent: false })
    revalidateControlTree(this.memberForm)
    this.memberForm.markAsPristine()
    if (result.member.name?.localValue !== null && result.member.name?.localValue !== undefined) {
      this.memberFormService.addAsyncValidators(this.memberForm, result.member.category, result.member.name.localValue)
    }
    if (result.policy) {
      this.policyForm.patchValue(result.policy, { emitEvent: false })
      this.policyForm.markAsPristine()
    }
    if (result.coverages && result.coverages.length > 0) {
      // Only the data changes after a save; the user stays where they are editing.
      this.updateCoverageSections(result.coverages, { keepNavigation: true })
    }
    this.sidebarState.reload().subscribe()

    const newUuid = result.member.uuid
    if (hasActualValue(newUuid) && this.route.snapshot.paramMap.get('uuid') !== newUuid) {
      this.navigateToInsured(newUuid)
    }
    this.cdr.markForCheck()
  }

  private confirmLeave(): Observable<boolean> {
    return this.modalHelper.confirmLeave(() => this.performSave$(), () => this.reportSaveError())
  }

  private setupRouteListener(): void {
    this.route.paramMap.pipe(
      map(pm => pm.get('uuid')),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(uuid => {
      const isCreate = this.route.snapshot.url.some(s => s.path === 'create')
      if (isCreate) {
        this.lastLoadedUuid = null
        this.resetCoverageForms()
        this.applyDefaults()
      } else if (uuid !== null && uuid !== this.lastLoadedUuid) {
        this.lastLoadedUuid = uuid
        this.resetCoverageForms()
        this.load(uuid)
      }
    })
  }

  /**
   * Clears every coverage form back to the mapper's empty defaults before a different insured
   * is loaded.
   *
   * The router reuses this component instance across `insured/:uuid` navigations and the coverage
   * forms are built once, so they keep the previous insured's values until something overwrites
   * them, and `buildSaveRequest` ships all three forms (including the previous insured's coverage
   * uuids) on the next autosave. Resetting up front covers the case where the reload chain never reaches
   * `loadCoverages` at all because the new insured has no policy in the active period.
   *
   * `emitEvent: false` keeps this out of the autosave stream: the forms are pristine again
   * immediately afterwards, so an emission here could only be read as a user edit.
   */
  private resetCoverageForms(): void {
    this.aviationHullForm.patchValue(this.coverageMapper.mapToAviationHull([]), { emitEvent: false })
    this.coverageFormService.patchAviationLiabilities(this.aviationLiabilitiesForm, this.coverageMapper.mapToAviationLiabilities([]), { emitEvent: false })
    this.commercialGeneralLiabilityForm.patchValue(this.coverageMapper.mapToCommercialGeneralLiability([]), { emitEvent: false })

    this.aviationHullForm.markAsPristine()
    this.aviationLiabilitiesForm.markAsPristine()
    this.commercialGeneralLiabilityForm.markAsPristine()

    this.availableCoverages.set([])
  }

  private updateInsuredDisplayName(): void {
    const nameValues = this.memberForm.controls.name.getRawValue()
    const finalValue = (nameValues.localValue ?? nameValues.externalValue) ?? ''
    const createModeValue = this.isCreateMode() ? 'New Member' : ''

    this.insuredDisplayName.set(finalValue === '' ? createModeValue : finalValue)
  }

  private applyDefaults(): void {
    this.insuredDataFacade.isCreateMode.set(true)
    const category = this.categoryService.activeCertificateCategory()
    this.memberFormService.applyCreateDefaults(this.memberForm, category!)
    this.memberForm.patchValue({
      name: {
        localValue: 'New Insured',
      },
      companyCode: {
        localValue: 'tba',
      },
      category: category
    })

    this.policyFormService.applyCreateDefaults(this.policyForm)

    this.updateInsuredDisplayName()
    this.updateCoverageSections([])
    this.cdr.markForCheck()
  }

  private setupPeriodAndPolicyListeners(): void {
    this.memberForm.controls.uuid.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe(memberUuid => {
        if (hasActualValue(memberUuid)) {
          const periodUuid = this.periodForm.controls.uuid.value
          if (hasActualValue(periodUuid)) {
            this.loadPolicies(memberUuid, periodUuid)
          }
        }
      })

    this.periodForm.controls.uuid.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe(periodUuid => {
        if (hasActualValue(periodUuid)) {
          const memberUuid = this.memberForm.controls.uuid.value
          if (hasActualValue(memberUuid)) {
            this.loadPolicies(memberUuid, periodUuid)
          }
        }
      })

    this.policyForm.controls.uuid.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe(policyUuid => {
        if (hasActualValue(policyUuid)) {
          const selectedPolicy = this.policies().find(p => p.uuid === policyUuid)
          if (hasActualValue(selectedPolicy)) {
            this.policyForm.patchValue(selectedPolicy, { emitEvent: false })
            this.policyForm.markAsPristine()
            this.loadCoverages(policyUuid)
          }
        }
      })
  }

  private loadPeriods(): void {
    this.insuredDataFacade.loadPeriods().subscribe({
      next: (periods) => {
        const activePeriod = periods.find(p => p.active) ?? periods[0]
        if (activePeriod) {
          this.periodForm.patchValue(activePeriod)
        }
      },
      error: () => {
        this.globalErrorService.reportError({
          subsystemName: 'Period Loading',
          description: 'The available periods could not be loaded.'
        })
      }
    })
  }

  private loadPolicies(memberUuid: string, periodUuid: string): void {
    this.insuredDataFacade.loadPolicies(memberUuid, periodUuid).subscribe({
      next: (policies) => {
        const firstPolicy = policies[0]
        if (firstPolicy) {
          this.policyForm.patchValue(firstPolicy)
          this.policyForm.markAsPristine()
        } else {
          this.policyForm.reset()
          this.updateCoverageSections([])
        }
      },
      error: () => {
        this.globalErrorService.reportError({
          subsystemName: 'Policy Loading',
          description: 'The policies for this member and period could not be loaded.'
        })
      }
    })
  }

  private loadCoverages(policyId: string): void {
    this.insuredDataFacade.loadCoverages(policyId).subscribe(apiCoverages => {
      this.updateCoverageSections(apiCoverages)
    })
  }

  /**
   * Shows the given coverages. On a load it also opens all panels and returns to the section the user was on;
   * after a save (`keepNavigation`) the open panels and the scroll position stay, since resetting them would
   * jump the page away from the field being edited.
   */
  private updateCoverageSections(apiCoverages: CoverageApiResponse[], options: { keepNavigation?: boolean } = {}): void {
    this.availableCoverages.set([...new Set(apiCoverages.map(c => c.coverageType))])

    // Server-driven hydration: patch then mark pristine so the autosave dirty-gate doesn't
    // mistake these writes for user edits and PUT everything straight back.
    this.aviationHullForm.patchValue(this.coverageMapper.mapToAviationHull(apiCoverages))
    this.aviationHullForm.markAsPristine()
    // After a save the entered war excess limits keep the order on screen; a load shows the backend's order.
    this.coverageFormService.patchAviationLiabilities(this.aviationLiabilitiesForm, this.coverageMapper.mapToAviationLiabilities(apiCoverages), {
      keepAircraftLimitOrder: options.keepNavigation === true
    })
    this.aviationLiabilitiesForm.markAsPristine()
    this.commercialGeneralLiabilityForm.patchValue(this.coverageMapper.mapToCommercialGeneralLiability(apiCoverages))
    this.commercialGeneralLiabilityForm.markAsPristine()

    const currentHeading = this.nav.sectionHeadings()[this.nav.currentIndex()]
    const navHeadings = this.coverageMapper.getNavSections()
    if (options.keepNavigation === true) {
      // A save can add a coverage type (e.g. the first saved sub-coverage), which needs its headings.
      if (!sameSections(this.nav.sectionHeadings(), navHeadings)) {
        this.nav.setSectionHeadings(navHeadings)
      }
      this.cdr.markForCheck()
      return
    }
    this.nav.setSectionHeadings(navHeadings)
    this.nav.setActivePanels(this.sections.flatMap((section, mainIndex) =>
      section.collapsedOnLoad ? [] : [mainIndex.toString()]))

    if (currentHeading) {
      const newIndex = navHeadings.findIndex(h => h.label === currentHeading.label)
      if (newIndex !== -1) {
        this.nav.goToSection(newIndex)
      }
    }
    this.cdr.markForCheck()
  }

  private toggleFormEnablement(readonly: boolean): void {
    const opts = { emitEvent: false }
    if (readonly) {
      this.aviationHullForm.disable(opts)
      this.aviationLiabilitiesForm.disable(opts)
      this.commercialGeneralLiabilityForm.disable(opts)
    } else {
      this.aviationHullForm.enable(opts)
      this.aviationLiabilitiesForm.enable(opts)
      this.commercialGeneralLiabilityForm.enable(opts)
      // `enable()` propagates to every descendant, which would re-activate the
      // read-only `externalValue` controls owned by AlbertValueObjectForm groups.
      // Re-disable them so origami fields stay non-editable even outside POLICY_READONLY.
      this.disableExternalValues(this.aviationHullForm)
      this.disableExternalValues(this.aviationLiabilitiesForm)
      this.disableExternalValues(this.commercialGeneralLiabilityForm)
    }
  }

  /**
   * Recursively disables every `externalValue` control under `control`. Used after
   * `FormGroup.enable()` to keep origami-sourced fields read-only regardless of the
   * surrounding form's enabled state.
   */
  private disableExternalValues(control: AbstractControl): void {
    if (isAlbertValueObjectForm(control)) {
      control.controls.externalValue.disable({ emitEvent: false })
      return
    }
    if (control instanceof FormGroup) {
      for (const key of Object.keys(control.controls)) {
        const child = control.get(key)
        if (child) this.disableExternalValues(child)
      }
    }
  }
}
